import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BootSpinner from './BootSpinner';

// Route guard for admin-only pages.
//
// Options:
//   - requireSuperadmin: strict superadmin-only (system settings, etc.)
//   - requireRoles: array of allowed userRoles. Use for routes shared
//     between specific roles (e.g. CRM is open to superadmin + salesperson).
//
// Non-authorized users (signed in but lacking the required role) get
// bounced to / instead of /login.
const ProtectedRoute = ({ children, requireSuperadmin = false, requireRoles = null }) => {
  const { currentUser, isAdmin, userRole, managerAssignments, authResolving } = useAuth();

  // The identity just changed (sign-in, or a god-mode session swap) and its
  // role and memberships are still loading. Deciding now would judge a mix of
  // two users, and a redirect to /login is not undone when the load finishes.
  if (authResolving) return <BootSpinner />;

  // Base access: must be signed in and have at least one privileged role, OR
  // hold an org_managers row. recomputeUserRole only promotes users.role to
  // org_owner/salesperson, so a plain manager stays role='user' — gating on
  // role alone locked them out of the app they were just invited to. This
  // matches the manager fallback AuthContext.signIn already applies.
  const isOrgManager = (managerAssignments || []).length > 0;
  const hasAccess = currentUser && (isAdmin || userRole === 'org_owner' || userRole === 'salesperson' || userRole === 'affiliate' || userRole === 'social_media' || isOrgManager);
  if (!hasAccess) {
    return <Navigate to="/login" replace />;
  }

  // Social media managers are a single-surface role: only routes that
  // explicitly allow 'social_media' in requireRoles are reachable. Everything
  // else — including the bare ProtectedRoute routes that any other org member
  // may load — bounces straight to their inbox. Without this, the role could
  // reach Sessions/Booths/etc. by URL even with the nav hidden.
  if (userRole === 'social_media' && !(Array.isArray(requireRoles) && requireRoles.includes('social_media'))) {
    return <Navigate to="/social-media" replace />;
  }

  // Stricter: superadmin-only routes.
  if (requireSuperadmin && userRole !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  // Role allow-list (use for routes shared by a specific set of roles).
  if (Array.isArray(requireRoles) && requireRoles.length > 0 && !requireRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;